
import { Component, Vue } from 'vue-property-decorator';
import Sudoku from '@/components/Sudoku.vue';
import Button from '@/components/Button.vue';
import SelectBox from '@/components/SelectBox.vue';
import Counters from '@/components/Counters.vue';

import { BoardUpdate } from '@/interfaces/BoardUpdate';
import { SelectBoxChange } from '@/interfaces/SelectBoxChange';
import GameTimer from '@/utils/GameTimer';
import FocusService from '@/utils/FocusService';
import { optsStore } from '@/utils/storage';
import { SudokuGrid } from '@/sudoku/interfaces/SudokuGrid';
import { SudokuError } from '@/sudoku/interfaces/SudokuError';
import generate from '@/sudoku/generate';
import solve from '@/sudoku/solve';
import getConflicts from '@/sudoku/getConflicts';
import { SQUARES } from '@/sudoku/consts';
import { Difficulty } from '@/sudoku/enums/Difficulty';

@Component({
  components: { Sudoku, Button, SelectBox, Counters }
})
export default class Home extends Vue {
  private initialGrid: SudokuGrid = {};
  private grid: SudokuGrid = {};
  private solution: SudokuGrid = {};
  private difficulty: Difficulty = Difficulty.easy;

  private inGame: boolean = false;
  private timeElapsed: string = '00m 00s';
  private userFeedback: string = `Let's play sudoku!`;
  private errors: SudokuError[] = [];
  private mistakes: string[] = [];
  private highlightNumber: number = -1;

  get difficultyOptions() {
    return Object.values(Difficulty).map((value) => ({
      value,
      text: value
    }));
  }

  public mounted() {
    const opts = optsStore.get();
    this.difficulty = opts.difficulty;
  }

  public onDifficultySelection(change: SelectBoxChange) {
    const { value } = change;
    const difficulty = value as Difficulty;
    this.difficulty = difficulty;
    optsStore.set({ difficulty });
  }

  public handleBoardUpdate(update: BoardUpdate) {
    this.$set(this.grid, update.square, update.value);
    // TEMP FOR SUCCESS DEV
    // this.grid = this.solution;
  }

  public onNewGame() {
    const grid = generate(this.difficulty);

    this.timeElapsed = '00m 00s';
    this.userFeedback = ``;
    this.initialGrid = { ...grid };
    this.grid = { ...grid };
    this.solution = solve({ ...grid });
    this.inGame = true;
    this.unsubTimer = GameTimer.subscribe((time) => (this.timeElapsed = time));
  }

  public setHighlightNumber(num: number) {
    this.highlightNumber = num;
  }

  public onCounterSelect(num: number) {
    const prevSquare = FocusService.getPreviousFocus();
    if (prevSquare === undefined) {
      return;
    }

    const initSquareValue = this.initialGrid[prevSquare];
    if (!initSquareValue) {
      // Only update the square if it isn't a pre-populated square.
      this.handleBoardUpdate({
        square: prevSquare,
        value: `${num}`
      });
    }

    this.$nextTick(() => document.getElementById(prevSquare)?.focus());
  }

  public onSubmit() {
    const solution = { ...this.solution };
    const currentGrid = { ...this.grid };

    const { errors, mistakes } = getConflicts(solution, currentGrid);
    const mistakeCount = mistakes.length;

    // Mistakes that break sudoku rules.
    if (errors.length) {
      this.errors = [...errors];
      this.userFeedback = `You've broken the rules!`;
      return;
    }

    this.errors = [];

    // Mistakes that don't break sudoku rules, but are incorrect to the solution
    if (mistakeCount) {
      const plu = mistakeCount !== 1 ? 's' : '';
      this.mistakes = [...mistakes];
      this.userFeedback = `There appear to be ${mistakeCount} mistake${plu}.`;
      return;
    }

    this.mistakes = [];
    const isSolved = Object.keys(solution).every(
      (k) => solution[k] === currentGrid[k]
    );

    if (isSolved) {
      this.completedGame();
    } else {
      const squaresLeft = SQUARES.filter((x) => !currentGrid[x]).length;
      this.userFeedback = `No conflicts found. ${squaresLeft} squares left.`;
    }
  }

  private completedGame() {
    const time = this.unsubTimer();
    this.userFeedback = `You've completed the puzzle!
      Completed in ${GameTimer.formatTime(time)}`;
    this.inGame = false;

    const opts = optsStore.get();
    optsStore.set({
      history: [
        ...opts.history,
        {
          datetime: new Date().getTime(),
          timeElapsed: time,
          difficulty: this.difficulty
        }
      ]
    });
  }

  private unsubTimer: () => number = () => 0;
}
